import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import EmptyState from '@/components/admin/EmptyState';
import { adminApi } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Trash2, Download, Search, ExternalLink, Phone, Mail, Calendar } from 'lucide-react';
import { STATUS_COLORS, STATUS_LABEL, SOURCES_LABEL } from '@/lib/data';
import { toast } from 'sonner';
import { format } from 'date-fns';

const niceDate = (iso) => { try { return format(new Date(iso), 'dd/MM/yyyy HH:mm'); } catch { return iso?.slice(0, 16); } };

export default function AdminLeadsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [openId, setOpenId] = useState(null);
    const [newNote, setNewNote] = useState('');

    const load = () => {
        setLoading(true);
        const params = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (q) params.q = q;
        adminApi.leads.list(params).then((data) => setItems(data)).finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filtered = useMemo(() => items, [items]);
    const current = items.find((l) => l.id === openId);

    const changeStatus = async (id, status) => {
        await adminApi.leads.update(id, { status });
        toast.success('Estado actualizado');
        load();
    };
    const addNote = async (id) => {
        if (!newNote.trim()) return;
        await adminApi.leads.addNote(id, newNote.trim());
        setNewNote('');
        const refreshed = await adminApi.leads.get(id);
        setItems((prev) => prev.map((l) => (l.id === id ? refreshed : l)));
    };
    const remove = async (id) => {
        await adminApi.leads.delete(id);
        toast.success('Lead eliminado');
        setOpenId(null);
        load();
    };
    const exportCsv = async () => {
        const blob = await adminApi.leads.exportCsv();
        const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const a = document.createElement('a'); a.href = url; a.download = 'leads.csv'; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <AdminLayout title="Leads / Contactos">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input data-testid="admin-leads-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Buscar por nombre, teléfono, email…" className="pl-9" />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(load, 0); }}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="admin-leads-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos los estados</SelectItem>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                </Select>
                <button onClick={exportCsv} data-testid="admin-leads-export-csv" className="inline-flex items-center gap-2 rounded-lg bg-secondary text-foreground px-3 py-2 text-sm font-medium hover:bg-secondary/80"><Download className="size-4" /> Exportar CSV</button>
            </div>
            {loading ? (
                <div className="rounded-2xl bg-white border border-border p-10 animate-pulse h-64" />
            ) : filtered.length === 0 ? (
                <EmptyState title="Sin leads aún" description="Cuando alguien envíe un formulario lo verás aquí." />
            ) : (
                <div className="rounded-2xl bg-white border border-border shadow-[var(--shadow-soft)] overflow-x-auto">
                    <table data-testid="admin-leads-table" className="min-w-full text-sm">
                        <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-3">Nombre</th>
                                <th className="text-left px-4 py-3">Contacto</th>
                                <th className="text-left px-4 py-3">Origen / Interés</th>
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-right px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filtered.map((l) => (
                                <tr key={l.id} className="hover:bg-secondary/50">
                                    <td className="px-4 py-3"><div className="font-medium">{l.name}</div><div className="text-xs text-muted-foreground">{l.preferred_channel}</div></td>
                                    <td className="px-4 py-3 text-xs">{l.phone && <div className="flex items-center gap-1"><Phone className="size-3" /> {l.phone}</div>}{l.email && <div className="flex items-center gap-1"><Mail className="size-3" /> {l.email}</div>}</td>
                                    <td className="px-4 py-3 text-xs"><div>{SOURCES_LABEL[l.source] || l.source}</div><div className="text-muted-foreground">{l.interest}</div></td>
                                    <td className="px-4 py-3 text-xs whitespace-nowrap">{niceDate(l.created_at)}</td>
                                    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-1 rounded-full border inline-block ${STATUS_COLORS[l.status] || ''}`}>{STATUS_LABEL[l.status] || l.status}</span></td>
                                    <td className="px-4 py-3 text-right"><button onClick={() => setOpenId(l.id)} data-testid={`lead-row-open-${l.id}`} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--kche-cyan))] hover:underline">Ver <ExternalLink className="size-3.5" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
                <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                    {current && (
                        <>
                            <SheetHeader>
                                <SheetTitle>{current.name}</SheetTitle>
                                <SheetDescription>Detalle del lead</SheetDescription>
                            </SheetHeader>
                            <div className="mt-4 grid gap-3 text-sm">
                                <div className="grid gap-1">
                                    {current.phone && <div className="flex items-center gap-2"><Phone className="size-4" /> {current.phone}</div>}
                                    {current.email && <div className="flex items-center gap-2"><Mail className="size-4" /> {current.email}</div>}
                                    {current.event_date && <div className="flex items-center gap-2"><Calendar className="size-4" /> {current.event_date}</div>}
                                    <div className="text-xs text-muted-foreground">Recibido: {niceDate(current.created_at)}</div>
                                </div>
                                <div>
                                    <Label>Origen</Label>
                                    <p className="mt-1 text-foreground">{SOURCES_LABEL[current.source] || current.source}</p>
                                </div>
                                <div>
                                    <Label>Interés</Label>
                                    <p className="mt-1 text-foreground">{current.interest || '—'}</p>
                                </div>
                                <div>
                                    <Label>Mensaje</Label>
                                    <p className="mt-1 whitespace-pre-wrap text-foreground">{current.message || '—'}</p>
                                </div>
                                <div>
                                    <Label>Estado</Label>
                                    <Select value={current.status} onValueChange={(v) => changeStatus(current.id, v)}>
                                        <SelectTrigger className="mt-1" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="border-t border-border pt-3">
                                    <h4 className="font-display font-semibold">Notas internas</h4>
                                    <ul className="mt-2 space-y-2">
                                        {(current.notes || []).map((n) => (
                                            <li key={n.id} className="rounded-lg bg-secondary p-2"><p className="text-sm whitespace-pre-wrap">{n.text}</p><p className="text-[10px] text-muted-foreground mt-1">{niceDate(n.created_at)} · {n.author}</p></li>
                                        ))}
                                        {(current.notes || []).length === 0 && <li className="text-xs text-muted-foreground">Sin notas aún.</li>}
                                    </ul>
                                    <Textarea data-testid="lead-note-input" rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} className="mt-2" placeholder="Añadir nota interna…" />
                                    <button onClick={() => addNote(current.id)} data-testid="lead-note-add" className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-3 py-1.5 text-sm">Añadir nota</button>
                                </div>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button className="mt-4 inline-flex items-center gap-2 rounded-lg bg-destructive text-destructive-foreground px-3 py-2 text-sm" data-testid="lead-delete-button"><Trash2 className="size-4" /> Eliminar lead</button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>¿Eliminar este lead?</AlertDialogTitle>
                                            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => remove(current.id)}>Eliminar</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </AdminLayout>
    );
}
